/**
 * Forecast growth options — phased organic signup growth with fall-flat / explode tails.
 * Sampling mirrors Economics (`sampleMonthlyGrowthRate` / lognormal).
 */

export const DEFAULT_FORECAST_GROWTH_PHASES = [
  {
    throughMonth: 6,
    monthlyGrowthPct: 11,
    volatilityPct: 45,
    label: 'Early (pre-PMF)',
  },
  {
    throughMonth: 18,
    monthlyGrowthPct: 7,
    volatilityPct: 22,
    label: 'Middle (traction)',
  },
  {
    throughMonth: 36,
    monthlyGrowthPct: 4,
    volatilityPct: 12,
    label: 'Late (scale)',
  },
];

/** Named growth shapes applied to pack.growth (not whole scenario). */
export const GROWTH_OPTION_PRESETS = {
  fall_flat: {
    label: 'Fall flat',
    hint: 'Low early mean, high vol — many paths stall or shrink',
    distribution: 'lognormal',
    mode: 'phased',
    phases: [
      { throughMonth: 6, monthlyGrowthPct: 2, volatilityPct: 55, label: 'Early (stall risk)' },
      { throughMonth: 18, monthlyGrowthPct: 1, volatilityPct: 30, label: 'Middle (flat)' },
      { throughMonth: 36, monthlyGrowthPct: 0.5, volatilityPct: 15, label: 'Late' },
    ],
  },
  traction: {
    label: 'Traction',
    hint: 'Base early-stage skew — climb with fat right tail',
    distribution: 'lognormal',
    mode: 'phased',
    phases: DEFAULT_FORECAST_GROWTH_PHASES.map((p) => ({ ...p })),
  },
  explode: {
    label: 'Explode',
    hint: 'High early mean + high vol — viral / breakout paths',
    distribution: 'lognormal',
    mode: 'phased',
    phases: [
      { throughMonth: 6, monthlyGrowthPct: 40, volatilityPct: 65, label: 'Early (breakout)' },
      { throughMonth: 18, monthlyGrowthPct: 18, volatilityPct: 35, label: 'Middle (hypergrowth)' },
      { throughMonth: 36, monthlyGrowthPct: 8, volatilityPct: 18, label: 'Late (scale)' },
    ],
  },
};

export function createDefaultGrowth(presetKey = 'traction') {
  const preset = GROWTH_OPTION_PRESETS[presetKey] || GROWTH_OPTION_PRESETS.traction;
  return {
    enabled: true,
    mode: preset.mode || 'phased',
    distribution: preset.distribution || 'lognormal',
    preset: presetKey,
    phases: (preset.phases || DEFAULT_FORECAST_GROWTH_PHASES).map((p) => ({ ...p })),
  };
}

/** Scenario-pack defaults: bear→fall flat, base→traction, bull→explode. */
export function growthForScenarioKey(scenarioKey) {
  if (scenarioKey === 'bear') return createDefaultGrowth('fall_flat');
  if (scenarioKey === 'bull') return createDefaultGrowth('explode');
  return createDefaultGrowth('traction');
}

export function applyGrowthOptionPreset(growth, presetKey) {
  const next = createDefaultGrowth(presetKey);
  return {
    ...growth,
    ...next,
    enabled: growth?.enabled !== false,
  };
}

/**
 * Keep imported/custom phase tables deterministic and safe for resolution.
 * The UI constrains these fields, but scenarios can also arrive from JSON.
 */
export function normalizeGrowth(growth = {}) {
  const safeGrowth = growth && typeof growth === 'object' ? growth : {};
  const enabled =
    safeGrowth.enabled === false ||
    safeGrowth.enabled === 0 ||
    safeGrowth.enabled === 'false'
      ? false
      : true;
  const source = Array.isArray(safeGrowth.phases) && safeGrowth.phases.length
    ? safeGrowth.phases
    : DEFAULT_FORECAST_GROWTH_PHASES;
  const phases = source
    .map((phase, index) => ({
      throughMonth: Math.min(
        60,
        Math.max(1, Math.floor(Number(phase?.throughMonth) || 1))
      ),
      monthlyGrowthPct: Number.isFinite(Number(phase?.monthlyGrowthPct))
        ? Math.min(1000, Math.max(-100, Number(phase.monthlyGrowthPct)))
        : 0,
      volatilityPct: Math.min(200, Math.max(0, Number(phase?.volatilityPct) || 0)),
      label: phase?.label || `Phase ${index + 1}`,
    }))
    .sort((a, b) => a.throughMonth - b.throughMonth);
  return {
    ...safeGrowth,
    enabled,
    mode: safeGrowth.mode === 'channel' ? 'channel' : 'phased',
    distribution: ['fixed', 'normal', 'lognormal'].includes(safeGrowth.distribution)
      ? safeGrowth.distribution
      : 'lognormal',
    phases,
  };
}

/** @param {object} growth @param {number} monthIndex — 1-based month after Now */
export function resolvePhase(growth, monthIndex) {
  const phases = normalizeGrowth(growth).phases;
  return phases.find((p) => monthIndex <= p.throughMonth) || phases[phases.length - 1];
}

function gaussian(rng) {
  let u = Number(rng());
  let v = Number(rng());
  if (!(u > 0)) u = Number.MIN_VALUE;
  if (!(v > 0)) v = Number.MIN_VALUE;
  u = Math.min(1, u);
  v = Math.min(1, v);
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
}

/**
 * Sample monthly organic signup growth rate (decimal, e.g. 0.1 = 10%).
 * Lognormal on (1+g): mean-corrected so E[g] ≈ phase.monthlyGrowthPct/100,
 * with fat right tail from volatilityPct.
 */
export function sampleMonthlyGrowthRate(phase, distribution, rng) {
  const rawMean = Number(phase?.monthlyGrowthPct);
  const rawVol = Number(phase?.volatilityPct);
  const mean = Math.min(10, Math.max(-0.95, (Number.isFinite(rawMean) ? rawMean : 0) / 100));
  const vol = Math.min(10, Math.max(0, (Number.isFinite(rawVol) ? rawVol : 0) / 100));

  if (!rng || distribution === 'fixed' || vol <= 0) {
    return mean;
  }

  if (distribution === 'normal') {
    return Math.max(-0.95, mean + vol * gaussian(rng));
  }

  const sigma = Math.max(0.01, vol);
  const meanFactor = Math.max(1 + mean, 0.001);
  // E[exp(μ+σZ)] = exp(μ+σ²/2) = meanFactor  →  μ = log(meanFactor) − σ²/2
  const mu = Math.log(meanFactor) - (sigma * sigma) / 2;
  return Math.exp(mu + sigma * gaussian(rng)) - 1;
}

/**
 * Growth rate to apply for month m (0 = Now).
 * @returns {number|null} decimal rate, or null to use channel.organic.growthPctMo
 */
export function resolveOrganicGrowthRate(growth, monthIndex, rng) {
  if (!growth || growth.enabled === false || growth.mode === 'channel') {
    return null;
  }
  if (monthIndex <= 0) return 0;

  const phase = resolvePhase(growth, monthIndex);
  const distribution = growth.distribution || 'lognormal';
  // Deterministic path (no rng): fixed phase mean. MC path: sample each month.
  return sampleMonthlyGrowthRate(phase, rng ? distribution : 'fixed', rng || null);
}

/** Default MC regime mix — early-stage: most paths traction, fat tails both ways. */
export const DEFAULT_GROWTH_REGIME_MIX = {
  fall_flat: 0.25,
  traction: 0.5,
  explode: 0.25,
};

/**
 * Normalize regime weights (fall_flat / traction / explode). Missing → defaults.
 * Set mixGrowthRegimes: false on monteCarlo to disable.
 */
export function normalizeGrowthRegimeMix(mix) {
  const src = mix && typeof mix === 'object' ? mix : DEFAULT_GROWTH_REGIME_MIX;
  const safeWeight = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
  };
  const fall = safeWeight(src.fall_flat);
  const traction = safeWeight(src.traction);
  const explode = safeWeight(src.explode);
  const sum = fall + traction + explode;
  if (sum <= 0) return { ...DEFAULT_GROWTH_REGIME_MIX };
  return {
    fall_flat: fall / sum,
    traction: traction / sum,
    explode: explode / sum,
  };
}

/**
 * Draw one growth regime for an MC run.
 * @param {() => number} rng — U(0,1)
 * @param {object} [mix]
 * @returns {'fall_flat'|'traction'|'explode'}
 */
export function sampleGrowthRegime(rng, mix) {
  const weights = normalizeGrowthRegimeMix(mix);
  const u = typeof rng === 'function' ? rng() : 0.5;
  if (u < weights.fall_flat) return 'fall_flat';
  if (u < weights.fall_flat + weights.traction) return 'traction';
  return 'explode';
}

/**
 * Which regime bucket the pack's Growth UI represents.
 * Custom phase edits map to traction so Mix still uses your table ~50% of runs.
 */
export function activeGrowthRegimeKey(growth) {
  const preset = growth?.preset;
  if (preset === 'fall_flat' || preset === 'explode' || preset === 'traction') {
    return preset;
  }
  // Edited phases set preset:'custom' — treat as the traction / base path.
  return 'traction';
}

/**
 * Pack for one MC draw — optionally swaps Growth preset (regime mix).
 * Keeps funnel/channels/cash assumptions from the active pack.
 *
 * Important: when the sampled regime matches the pack's active Growth preset
 * (or custom → traction), keep the pack's phases so Growth-tab edits affect Results.
 * Other regimes still use stock Fall flat / Explode presets for P10/P90 span.
 */
export function packWithGrowthRegime(pack, regimeKey) {
  if (!regimeKey || !GROWTH_OPTION_PRESETS[regimeKey]) return pack;
  if (regimeKey === activeGrowthRegimeKey(pack?.growth)) {
    return pack;
  }
  return {
    ...pack,
    growth: applyGrowthOptionPreset(pack?.growth, regimeKey),
  };
}
